import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import axios from 'axios'
import { CheckCircle2, Copy, FileText, FlaskConical, PackagePlus } from 'lucide-react'
import { api, errorMessage, fieldErrors } from '@/lib/api'
import { useShippingInfo } from '@/lib/addresses'
import {
  DESCRIPTION_MAX,
  DESCRIPTION_MIN,
  WEIGHT_MAX_KG,
  WEIGHT_MIN_KG,
  bookingFieldFor,
  partyLine,
} from '@/lib/shipment'
import { cn } from '@/lib/cn'
import type { BookingParty, BookingPrefill, Order, Shipment } from '@/types'
import Button from '@/components/ui/Button'
import { Field, Input } from '@/components/ui/Field'
import { Select } from '@/components/ui/Select'
import Modal from '@/components/ui/Modal'
import Skeleton from '@/components/ui/Skeleton'
import { useToast } from '@/components/ui/toast'
import ShipmentStatusBadge from '@/components/order/ShipmentStatusBadge'
import TrackingLinkBox from '@/components/order/TrackingLinkBox'

interface Form {
  receiver: BookingParty
  weight_kg: string
  item_description: string
}

const emptyParty: BookingParty = { recipient_name: '', phone: '', line1: '', line2: '', city: '', postcode: '', state: '' }

/**
 * Books a consignment with the courier. Its own modal rather than a branch of
 * ShipmentModal: Save there is idempotent and reversible, Book here spends
 * money and cannot be undone from this screen, and the manual editor has to
 * keep working after a booking exists.
 *
 * The admin confirms every field the booking will use before anything is
 * sent. The receiver is prefilled from the order's delivery address and must
 * be typed in when there is none (admin walk-ins); the sender is read-only
 * from Settings > Delivery; the description is generated server-side within
 * the carrier's 20-50 character rule and editable.
 */
export default function BookShipmentModal({
  order,
  basePath,
  onClose,
}: {
  order: Order | null
  basePath: '/admin' | '/sales'
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const shippingInfo = useShippingInfo()

  const [phase, setPhase] = useState<'form' | 'done'>('form')
  const [form, setForm] = useState<Form>({ receiver: emptyParty, weight_kg: '', item_description: '' })
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [general, setGeneral] = useState<string[]>([])
  const [result, setResult] = useState<Shipment | null>(null)
  const [hadReceiver, setHadReceiver] = useState(true)

  // Fetched fresh on every open: the sender and the description are whatever
  // the booking would use right now, not what a cached copy says.
  const prefill = useQuery({
    queryKey: ['shipment-prefill', order?.id],
    queryFn: async () =>
      (await api.get<{ data: BookingPrefill }>(`${basePath}/orders/${order!.id}/shipments/prefill`)).data.data,
    enabled: Boolean(order),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  })

  useEffect(() => {
    setPhase('form')
    setForm({ receiver: emptyParty, weight_kg: '', item_description: '' })
    setErrors({})
    setGeneral([])
    setResult(null)
  }, [order?.id])

  useEffect(() => {
    if (!prefill.data) return
    setForm({
      receiver: prefill.data.receiver ? { ...emptyParty, ...prefill.data.receiver, line2: prefill.data.receiver.line2 ?? '' } : emptyParty,
      weight_kg: String(prefill.data.weight_kg),
      item_description: prefill.data.item_description,
    })
    setHadReceiver(prefill.data.receiver !== null)
  }, [prefill.data])

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['order-detail'] })
    queryClient.invalidateQueries({ queryKey: ['orders-page'] })
  }

  const book = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ data: Order; message?: string }>(`${basePath}/orders/${order!.id}/shipments`, {
          receiver: form.receiver,
          weight_kg: Number(form.weight_kg),
          item_description: form.item_description.trim(),
        })
      ).data,
    onSuccess: (res) => {
      invalidate()
      setResult(res.data.shipment ?? null)
      setPhase('done')
      toast.success(res.message ?? 'Booked')
    },
    onError: (err) => {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined

      if (status === 409) {
        toast.info('Already booked', errorMessage(err))
        invalidate()
        onClose()
        return
      }

      if (status === 422) {
        // The carrier's own reasons arrive under `shipment`. Attach each to a
        // field when its wording names one, so "postcode does not match
        // state" lands next to the postcode; the rest stay as a list.
        const { shipment: reasons = [], ...fields } = fieldErrors(err)
        const mapped: Record<string, string[]> = { ...fields }
        const rest: string[] = []
        for (const reason of reasons) {
          const key = bookingFieldFor(reason)
          if (key) (mapped[key] ??= []).push(reason)
          else rest.push(reason)
        }
        setErrors(mapped)
        setGeneral(rest)
        return
      }

      toast.error('Booking failed', errorMessage(err))
    },
  })

  const refreshLabel = useMutation({
    mutationFn: async () =>
      (await api.post<{ label_pdf_url: string }>(`${basePath}/orders/${order!.id}/shipments/${result!.id}/label`)).data,
    onSuccess: (res) => {
      setResult((current) => (current ? { ...current, label_pdf_url: res.label_pdf_url } : current))
      invalidate()
    },
    onError: (err) => toast.error('Label not ready', errorMessage(err)),
  })

  const setReceiver = (patch: Partial<BookingParty>) =>
    setForm((current) => ({ ...current, receiver: { ...current.receiver, ...patch } }))
  const error = (key: string) => errors[key]?.[0]

  const receiver = form.receiver
  const weight = Number(form.weight_kg)
  const descriptionLength = form.item_description.trim().length
  const descriptionOk = descriptionLength >= DESCRIPTION_MIN && descriptionLength <= DESCRIPTION_MAX
  const weightOk = Number.isFinite(weight) && weight >= WEIGHT_MIN_KG && weight <= WEIGHT_MAX_KG
  const valid =
    Boolean(order) &&
    prefill.data?.sender_complete === true &&
    receiver.recipient_name.trim() !== '' &&
    receiver.phone.trim() !== '' &&
    receiver.line1.trim().length >= 5 &&
    receiver.city.trim() !== '' &&
    /^\d{5}$/.test(receiver.postcode) &&
    receiver.state !== '' &&
    weightOk &&
    descriptionOk

  const stateOptions = (shippingInfo.data?.states ?? []).map((state) => ({ value: state, label: state }))

  return (
    <Modal
      open={Boolean(order)}
      onClose={() => {
        if (!book.isPending) onClose()
      }}
      title={order ? (phase === 'done' ? `Booked - ${order.number}` : `Book with Pos Laju - ${order.number}`) : ''}
      footer={
        phase === 'done' ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={book.isPending}>
              Cancel
            </Button>
            <Button loading={book.isPending} disabled={!valid} onClick={() => book.mutate()}>
              <PackagePlus className="size-4" /> Book with Pos Laju
            </Button>
          </>
        )
      }
    >
      {!order ? null : phase === 'done' && result ? (
        <div className="space-y-4">
          <p className="inline-flex items-center gap-2 font-semibold text-status-success">
            <CheckCircle2 className="size-5" /> Booked with {result.carrier}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm font-bold">{result.tracking_number}</span>
            <Button
              variant="ghost"
              className="px-2"
              onClick={async () => {
                await navigator.clipboard.writeText(result.tracking_number)
                toast.success('Tracking number copied')
              }}
            >
              <Copy className="size-4" />
            </Button>
            <ShipmentStatusBadge status={result.status} />
          </div>
          {result.label_pdf_url ? (
            <a href={result.label_pdf_url} target="_blank" rel="noreferrer" className="block">
              <Button className="w-full">
                <FileText className="size-4" /> Download label (PDF)
              </Button>
            </a>
          ) : (
            <div className="rounded-lg border border-line bg-app p-3 text-xs">
              <p className="text-muted">The booking went through but the label could not be fetched yet.</p>
              <Button variant="outline" className="mt-2" loading={refreshLabel.isPending} onClick={() => refreshLabel.mutate()}>
                <FileText className="size-4" /> Fetch label again
              </Button>
            </div>
          )}
          {order.job?.share_token && <TrackingLinkBox shareToken={order.job.share_token} />}
        </div>
      ) : prefill.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-24" />
        </div>
      ) : prefill.isError ? (
        <p className="rounded-lg border border-danger/60 bg-danger/20 p-3 text-sm text-danger">{errorMessage(prefill.error)}</p>
      ) : (
        <div className="space-y-4">
          {prefill.data?.mode === 'mock' && (
            <p className="inline-flex items-start gap-2 rounded-lg border border-warning/60 bg-warning/20 p-3 text-xs text-warning">
              <FlaskConical className="mt-0.5 size-4 shrink-0" />
              <span>Mock carrier - no real consignment is created. Switch the mode under Integrations once the Pos Laju credentials are in.</span>
            </p>
          )}

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted">Receiver</p>
            {!hadReceiver && (
              <p className="mt-1 text-xs text-muted">This order has no delivery address on file - enter where the parcel goes.</p>
            )}
            <div className="mt-2 grid grid-cols-2 gap-3 sm:gap-4">
              <Field label="Recipient name" htmlFor="book-name" error={error('receiver.recipient_name')} className="col-span-2">
                <Input id="book-name" className="min-w-0" value={receiver.recipient_name} onChange={(e) => setReceiver({ recipient_name: e.target.value })} />
              </Field>
              <Field label="Phone" htmlFor="book-phone" error={error('receiver.phone')}>
                <Input id="book-phone" className="min-w-0" inputMode="tel" value={receiver.phone} onChange={(e) => setReceiver({ phone: e.target.value })} />
              </Field>
              <Field label="Postcode" htmlFor="book-postcode" error={error('receiver.postcode')}>
                <Input
                  id="book-postcode"
                  className="min-w-0"
                  inputMode="numeric"
                  maxLength={5}
                  value={receiver.postcode}
                  onChange={(e) => setReceiver({ postcode: e.target.value.replace(/\D/g, '').slice(0, 5) })}
                />
              </Field>
              <Field label="Street address" htmlFor="book-line1" error={error('receiver.line1')} className="col-span-2">
                <Input id="book-line1" className="min-w-0" value={receiver.line1} onChange={(e) => setReceiver({ line1: e.target.value })} />
              </Field>
              <Field label="Address line 2 (optional)" htmlFor="book-line2" error={error('receiver.line2')} className="col-span-2">
                <Input id="book-line2" className="min-w-0" value={receiver.line2 ?? ''} onChange={(e) => setReceiver({ line2: e.target.value })} />
              </Field>
              <Field label="City" htmlFor="book-city" error={error('receiver.city')}>
                <Input id="book-city" className="min-w-0" value={receiver.city} onChange={(e) => setReceiver({ city: e.target.value })} />
              </Field>
              <Field label="State" htmlFor="book-state" error={error('receiver.state')}>
                <Select id="book-state" value={receiver.state} onChange={(value) => setReceiver({ state: value })} placeholder="Select state" options={stateOptions} />
              </Field>
            </div>
          </div>

          {prefill.data && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted">Sender</p>
              <div className="mt-2 rounded-lg border border-line bg-app p-3 text-xs">
                <p className="font-semibold">
                  {prefill.data.sender.recipient_name || 'Shop name not set'}
                  {prefill.data.sender.phone ? ` - ${prefill.data.sender.phone}` : ''}
                </p>
                <p className="mt-0.5 text-muted">{partyLine(prefill.data.sender) || 'Address not set'}</p>
                {prefill.data.sender_complete ? (
                  <Link to="/admin/settings/delivery?tab=pickup" className="mt-1 inline-block font-semibold text-primary hover:underline">
                    Change
                  </Link>
                ) : (
                  <p className="mt-1 font-semibold text-status-danger">
                    Set the shop's phone, city, state and postcode under{' '}
                    <Link to="/admin/settings/delivery?tab=pickup" className="underline">
                      Settings &gt; Delivery
                    </Link>{' '}
                    first.
                  </p>
                )}
              </div>
            </div>
          )}

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted">Parcel</p>
            <div className="mt-2 grid gap-3 sm:grid-cols-[8rem_minmax(0,1fr)]">
              <Field label="Weight (kg)" htmlFor="book-weight" error={error('weight_kg') ?? (form.weight_kg !== '' && !weightOk ? `Between ${WEIGHT_MIN_KG} and ${WEIGHT_MAX_KG} kg` : undefined)}>
                <Input
                  id="book-weight"
                  className="min-w-0"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min={WEIGHT_MIN_KG}
                  max={WEIGHT_MAX_KG}
                  value={form.weight_kg}
                  onChange={(e) => setForm((current) => ({ ...current, weight_kg: e.target.value }))}
                />
              </Field>
              <Field label="Contents description" htmlFor="book-description" error={error('item_description')}>
                <Input
                  id="book-description"
                  className="min-w-0"
                  maxLength={DESCRIPTION_MAX}
                  value={form.item_description}
                  onChange={(e) => setForm((current) => ({ ...current, item_description: e.target.value }))}
                />
                <p className={cn('text-2xs', descriptionOk ? 'text-muted' : 'text-status-danger')}>
                  {descriptionLength}/{DESCRIPTION_MAX} - Pos Laju needs {DESCRIPTION_MIN} to {DESCRIPTION_MAX} characters.
                </p>
              </Field>
            </div>
            {order.delivery_weight_g != null && (
              <p className="mt-1 text-2xs text-muted">From the product weights: {(order.delivery_weight_g / 1000).toFixed(2)} kg.</p>
            )}
          </div>

          <div className="rounded-lg bg-app p-3 text-sm">
            <p>
              <span className="font-semibold">Pos Laju</span>, {form.weight_kg || '?'} kg, to{' '}
              <span className="font-semibold">{receiver.recipient_name || '...'}</span>
              {receiver.postcode || receiver.city ? `, ${receiver.postcode} ${receiver.city}`.trimEnd() : ''}
              {receiver.state ? `, ${receiver.state}` : ''}
            </p>
            <p className="mt-1 text-xs text-muted">
              Creates a real consignment on the shop's Pos Laju account. It cannot be cancelled from this screen.
            </p>
          </div>

          {general.length > 0 && (
            <div className="rounded-lg border border-danger/60 bg-danger/20 p-3 text-xs text-danger">
              <p className="font-semibold">Pos Laju did not accept the booking:</p>
              <ul className="mt-1 list-disc space-y-1 pl-4">
                {general.map((reason) => (
                  <li key={reason} className="break-words">
                    {reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
